import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, User, Phone, Calendar, X } from 'lucide-react';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', birthDate: '' });

  useEffect(() => { fetchPatients(); }, []);

  const fetchPatients = () => {
    fetch('/api/patients')
      .then(res => res.json())
      .then(data => setPatients(data));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = editingPatient ? `/api/patients/${editingPatient.id}` : '/api/patients';
    const method = editingPatient ? 'PUT' : 'POST';
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    }).then(() => {
      fetchPatients();
      handleCancel();
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name,
      phone: patient.phone || '',
      birthDate: patient.birth_date || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPatient(null);
    setFormData({ name: '', phone: '', birthDate: '' });
  };

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Пациенты</h1>
          <p className="text-slate-500 mt-0.5">База данных пациентов</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Добавить пациента</span>
        </button>
      </div>

      {/* Поиск */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Поиск пациента по имени..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      {/* Список */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">
            Список пациентов
            <span className="ml-2 text-sm font-normal text-slate-500">({filteredPatients.length})</span>
          </h2>
        </div>

        {filteredPatients.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <User className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Пациентов не найдено</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredPatients.map((patient) => (
              <div key={patient.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-4 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-slate-900">{patient.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                        {patient.phone && (
                          <span className="flex items-center space-x-1 text-sm text-slate-500">
                            <Phone className="h-3.5 w-3.5" />
                            <span>{patient.phone}</span>
                          </span>
                        )}
                        {patient.birth_date && (
                          <span className="flex items-center space-x-1 text-sm text-slate-500">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{formatDate(patient.birth_date)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(patient)}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-700 rounded-lg transition-colors text-sm font-medium flex-shrink-0"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="hidden sm:inline">Изменить</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-y-auto max-h-[92vh] shadow-2xl">
            <div className="bg-blue-800 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingPatient ? 'Редактировать пациента' : 'Добавить пациента'}
              </h3>
              <button onClick={handleCancel} className="text-blue-300 hover:text-white p-1 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Полное имя *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Фамилия Имя Отчество"
                  required
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Телефон *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="+7 (999) 000-00-00"
                  required
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Дата рождения</label>
                <input
                  type="text"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Год (1960) или дата (ДД.ММ.ГГГГ)"
                />
                <p className="text-sm text-slate-400 mt-1.5">Можно указать только год или полную дату</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-3.5 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
                >
                  {editingPatient ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
